'use client';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { useToast } from '@/components/ui/use-toast';
import { IReview, ReviewStatusEnum } from '@/entities/review/types';
import { queryClient } from '@/shared/api/query-client';
import { useSession } from '@/shared/hooks/use-session';
import { getApiUrl } from '@/shared/utils/getApiUrl';

export const ADMIN_REVIEWS_PAGE_SIZE = 100;

export interface IAdminReviewsPage {
  results: IReview[];
  pagination: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
}

/** Без `status` — все отзывы */
export const useGetAllReviewsQuery = ({
  status,
  page,
}: {
  status?: ReviewStatusEnum;
  page: number;
}) => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(ADMIN_REVIEWS_PAGE_SIZE),
  });
  if (status) {
    params.set('status', status);
  }
  const { token } = useSession();
  return useQuery({
    queryKey: ['allReviews', status ?? 'all', page],
    queryFn: () => {
      return axios.get(getApiUrl(`/review/all?${params}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    select: (data: { data: IAdminReviewsPage }) => data.data,
    placeholderData: keepPreviousData,
  });
};

const invalidateAdminReviews = () => {
  queryClient.invalidateQueries({ queryKey: ['allReviews'] });
  queryClient.invalidateQueries({ queryKey: ['allReviewsCount'] });
  // Отзывы и рейтинг на странице мастер-класса
  queryClient.invalidateQueries({ queryKey: ['masterclass'] });
};

export const useGetAllReviewsCountQuery = ({
  status,
}: {
  status: ReviewStatusEnum;
}) => {
  const { token } = useSession();
  return useQuery({
    queryKey: ['allReviewsCount', status],
    queryFn: () => {
      return axios.get(getApiUrl(`/review/all/count?status=${status}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    select: (data: { data: number }) => data.data,
  });
};

export const useApproveReviewMutation = () => {
  const { token } = useSession();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: { reviewId: number }) => {
      return await axios.put(
        getApiUrl(`/review/update`),
        {
          id: data.reviewId,
          status: ReviewStatusEnum.APPROVED,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      invalidateAdminReviews();
      return toast({
        title: 'Отзыв одобрен',
        variant: 'success',
      });
    },
  });
};

export const useRejectReviewMutation = () => {
  const { token } = useSession();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: { reviewId: number; rejectReason: string }) => {
      return await axios.put(
        getApiUrl(`/review/update`),
        {
          id: data.reviewId,
          status: ReviewStatusEnum.REJECTED,
          rejectReason: data.rejectReason,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
    },
    onSuccess: () => {
      invalidateAdminReviews();
      return toast({
        title: 'Отзыв отклонен',
        variant: 'error',
      });
    },
  });
};
